class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupLocaleSwitch();
    this._localeCartSnapshotReconciled = false;
    this._localeCartSnapshotRevision = 0;
    this.dispatchEvent(new CustomEvent('cartdrawer:constructed', { bubbles: true }));
  }

  connectedCallback(){
    this.bindCheckoutGuard();
  }

  disconnectedCallback() {
    if (this._onCheckoutClick) {
      document.removeEventListener('click', this._onCheckoutClick, true);
    }
    this._checkoutGuardBound = false;
  }

  bindCheckoutGuard() {
    if (this.dataset.checkoutGuardBound === 'true') return;
    this.dataset.checkoutGuardBound = 'true';

    if (this._onCheckoutClick) return

    this._onCheckoutClick = async (e) => {
      const btn = e.target.closest('[data-checkout-button]');
      if (!btn) return;

      const hasCustomKit = this.hasCustomKitInCart();
      if (!hasCustomKit) {
        e.preventDefault();
        e.stopPropagation();
        window.location.assign('/checkout');
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const result =
      (await this.blockCheckoutForCustomKit?.()) ?? { blocked: false };

      if (result?.blocked) {
        return;
      }

      window.location.assign('/checkout');
    }

    document.addEventListener('click', this._onCheckoutClick, true);
  };


  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Don't open mini bag if build kit confirmation modal is open
    const buildKitModal = document.getElementById('build-kit-add-to-bag-modal');
    if (buildKitModal && buildKitModal.open) {
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);

    if (!history.state || !history.state.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.checkForRestrictedItems();
    this.checkCustomKitItemAvailability();

    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.dispatchEvent(new CustomEvent('cartdrawer:open', { bubbles: true }));
      this.updateCheckoutAvailabilityState();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    const drawerInner = this.querySelector('.drawer__inner');
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
     if (drawerInner.scrollHeight > viewportHeight) {
      this.querySelector('.drawer__footer-border-top').style.display = 'block';
     }

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, fromKit, shouldOpen = true) {
    this.classList.contains('is-empty') &&
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      if (!fromKit) {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        if (shouldOpen) {
          this.open();
        }
      }
      this.checkForRestrictedItems();
      document.dispatchEvent(new CustomEvent('cart-drawer:rendered'));
      this.updateCheckoutAvailabilityState();
    });
  }

  renderContentsFreeSamples(parsedState) {
    this.classList.contains('is-empty') &&
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.freeSamplesSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.updateCheckoutAvailabilityState();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getUnavailableCountFromDom() {
    const unavailableEl = document.getElementById('CartDrawer-UnavailableCount');
    const count = parseInt(unavailableEl?.dataset?.unavailableCount, 10);
    return Number.isNaN(count) ? null : count;
  }

  hasCustomKitInCart() {
    const cartData = window.getTarteCartData?.() || null;
    if (!cartData?.items) return false;

    const hasCustomKit = cartData.items.some((item) => {
      const url = item?.custom_kit_url;
      return typeof url === 'string' && url.trim() !== '';
    });

    window.customKitInCart = hasCustomKit;
    return hasCustomKit;
  }

  async updateCheckoutAvailabilityState() {
    const unavailableCount = this.getUnavailableCountFromDom();
    const hasUnavailable = unavailableCount > 0;
    const checkoutButtons = document.querySelectorAll('#CartDrawer-Checkout');
    const oosHeader = this.querySelector('.cart-items-oos__wrapper');

    const isAvailable = (value) => String(value).toLowerCase() !== 'false';

    const expandBundleForOos = (cartItem) => {
      const bundleContainer = cartItem.querySelector('.cart-item__bundle-items');
      const toggleButton = cartItem.querySelector('.cart-item__bundle-button');
      const isHidden = bundleContainer?.classList.contains('hidden');

      if (isHidden) {
        if (typeof window.toggleBundleItems === 'function' && toggleButton) {
          window.toggleBundleItems(toggleButton);
          return;
        }
        bundleContainer.classList.remove('hidden');
        toggleButton?.classList.add('expanded');
      }
    };

    const resetLineItemState = () => {
      this.querySelectorAll('.cart-item__name, .cart-item__bundle-title, .product-option.variant dd, .cart-item__bundle-variant-title').forEach((el) => {
        el.classList.remove('cart-line-item__title-oos', 'cart-line-item__shade-oos');
        el.style.color = '';
      });
      this.querySelectorAll('.cart-line-item__oos').forEach((el) => {
        el.style.display = '';
        el.style.color = '';
      });
      this.querySelectorAll('.product-option.variant, .cart-item__bundle-variant-title').forEach((el) => {
        el.style.display = '';
      });
    };

    const setCheckoutButtons = (isDisabled) => {
      checkoutButtons.forEach((button) => {
        button.classList.toggle('disabled', isDisabled);
        button.toggleAttribute('disabled', isDisabled);
        button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        button.setAttribute('href', isDisabled ? '#' : '/checkout');
      });
    };

    resetLineItemState();

    const cartRows = this.querySelectorAll('.cart-item');

    setCheckoutButtons(hasUnavailable);

    checkoutButtons.forEach((button) => {
      if (button.dataset.unavailableClickBound === 'true') return;

      if (typeof window.updateCartEditLinks === 'function') {
        window.updateCartEditLinks();
      }
      button.dataset.unavailableClickBound = 'true';
    });

    if (oosHeader) {
      oosHeader.style.display = hasUnavailable ? 'flex' : 'none';
      if (hasUnavailable) {
        oosHeader.style.color = '#d1224b';
      }
    }

    if (!hasUnavailable) return;

    cartRows.forEach((cartItem) => {
      const isBundle = cartItem.dataset.isBundle === 'true';

      if (!isBundle) {
        const productAvailable = isAvailable(cartItem.dataset.cartItemAvailable);
        const variantEl = cartItem.querySelector('.product-option.variant[data-cart-item-available]');
        const variantAvailable = variantEl ? isAvailable(variantEl.dataset.cartItemAvailable) : productAvailable;

        if (!productAvailable) {
          cartItem.querySelector('.cart-item__name')?.classList.add('cart-line-item__title-oos');
          cartItem.querySelectorAll('.cart-line-item__oos').forEach((el) => {
            el.style.display = 'inline-block';
          });
          cartItem.querySelectorAll('.product-option.variant').forEach((oosMessage) => oosMessage.style.display = 'none' )
        } else if (!variantAvailable) {
          cartItem.querySelectorAll('.product-option.variant dd').forEach((dd) => dd.classList.add('cart-line-item__shade-oos'));
          cartItem.querySelectorAll('.product-option.variant .cart-line-item__oos').forEach((el) => {
            el.style.display = 'inline-block';
          });
        }
        return;
      }

      const bundleEntries = Array.from(cartItem.querySelectorAll('.cart-item__bundle-entry'));
      if (!bundleEntries.length) return;

      let allEntriesSoldOut = true;
      let bundleHasUnavailable = false;

      bundleEntries.forEach((entry) => {
        const entryProductAvailable = isAvailable(entry.dataset.cartItemAvailable);
        const variantTitle = entry.querySelector('.cart-item__bundle-variant-title');
        const variantAvailable = variantTitle ? isAvailable(variantTitle.dataset.cartItemAvailable) : entryProductAvailable;

        if (entryProductAvailable) {
          allEntriesSoldOut = false;
        }

        if (!entryProductAvailable) {
          const bundleTitle = entry.querySelector('.cart-item__bundle-title');
          if (bundleTitle) {
            bundleTitle.classList.add('cart-line-item__title-oos');
            bundleTitle.style.color = '#d1224b';
          }
          entry.querySelectorAll('.cart-line-item__oos').forEach((el) => {
            el.style.display = 'inline-block';
          });
          if (variantTitle) {
            variantTitle.style.display = 'none';
          }
          bundleHasUnavailable = true;
        } else if (!variantAvailable) {
          if (variantTitle) {
            variantTitle.classList.add('cart-line-item__shade-oos');
            variantTitle.style.color = '#d1224b';
          }
          allEntriesSoldOut = false;
          entry.querySelectorAll('.cart-line-item__oos').forEach((el) => {
            el.style.display = 'inline-block';
            el.style.color = '#d1224b';
          });
          bundleHasUnavailable = true;
        }
      });

      if (allEntriesSoldOut) {
        cartItem.querySelector('.cart-item__name')?.classList.add('cart-line-item__title-oos');
      }
      if (bundleHasUnavailable) {
        expandBundleForOos(cartItem);
      }
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  freeSamplesSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
      },
      {
        selector: 'drawer__footer'
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  checkForRestrictedItems() {
    if (this.isInternationalLocale()) {
      this.hideRestrictedItems();
    } else {
      this.restoreRestrictedItems();
    }
  }

  hideRestrictedItems() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const giftCardItems = cart.items.filter(item =>
          item.product_type === 'Gift Card' ||
          item.gift_card === true
        );

        const donationItems = cart.items.filter(item =>
          item.product_type === 'Donation'
        );

        const itemsToRemove = [...giftCardItems, ...donationItems];

        if (itemsToRemove.length > 0) {
          if (giftCardItems.length > 0) {
            const storedGiftCards = giftCardItems.map(item => ({
              variant_id: item.variant_id,
              quantity: item.quantity,
              properties: item.properties || {}
            }));
            localStorage.setItem('hiddenGiftCards', JSON.stringify(storedGiftCards));
          }

          if (donationItems.length > 0) {
            const storedDonations = donationItems.map(item => ({
              variant_id: item.variant_id,
              quantity: item.quantity,
              properties: item.properties || {}
            }));
            localStorage.setItem('hiddenDonations', JSON.stringify(storedDonations));
          }

          const updates = {};
          itemsToRemove.forEach(item => {
            updates[item.key] = 0;
          });

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates: updates })
          })
          .then(response => response.json())
          .then(updatedCart => {
            const nonRestrictedItems = updatedCart.items.filter(item =>
              item.product_type !== 'Gift Card' &&
              !item.gift_card &&
              item.product_type !== 'Donation'
            );

            if (nonRestrictedItems.length === 0) {
              this.classList.add('is-empty');
              const cartDrawerItems = document.querySelector('cart-drawer-items');
              if (cartDrawerItems) {
                cartDrawerItems.classList.add('is-empty');
              }
            }
            this.refreshCartDrawer();
          });
        }
      })
      .catch(error => {
        console.error('Error hiding restricted items:', error);
      });
  }

  restoreRestrictedItems() {
    const storedGiftCards = localStorage.getItem('hiddenGiftCards');
    const storedDonations = localStorage.getItem('hiddenDonations');

    if (!storedGiftCards && !storedDonations) {
      return;
    }

    const restorePromises = [];

    try {
      if (storedGiftCards) {
        const giftCardData = JSON.parse(storedGiftCards);
        restorePromises.push(
          new Promise((resolve) => {
            this.bulkAddGiftCards(giftCardData, () => {
              resolve();
            });
          })
        );
      }

      if (storedDonations) {
        console.log('Restoring donations...');
        const donationData = JSON.parse(storedDonations);
        restorePromises.push(
          new Promise((resolve) => {
            this.bulkAddDonations(donationData, () => {
              resolve();
            });
          })
        );
      }

      if (restorePromises.length > 0) {
        Promise.all(restorePromises).then(() => {
          this.refreshCartDrawer();
        });
      }
    } catch (error) {
      console.error('Error parsing stored restricted items:', error);
      localStorage.removeItem('hiddenGiftCards');
      localStorage.removeItem('hiddenDonations');
    }
  }

  bulkAddGiftCards(giftCardData, callback) {
    const items = giftCardData.map(item => ({
      id: item.variant_id,
      quantity: item.quantity,
      properties: item.properties || {}
    }));

    console.log('Bulk adding gift cards:', items);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: items
      })
    })
    .then(response => response.json())
    .then(addResult => {
      console.log('All gift cards restored successfully via bulk add:', addResult);
      localStorage.removeItem('hiddenGiftCards');

      if (addResult.items && addResult.items.length > 0) {
        this.classList.remove('is-empty');
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          cartDrawerItems.classList.remove('is-empty');
        }
      }

      if (callback) {
        callback();
        return Promise.resolve();
      }

      return fetch(`${window.routes.cart_url}?sections=cart-drawer,header`);
    })
    .then(response => {
      if (response && response.json) {
        return response.json();
      }
      return null;
    })
    .then(sections => {
      if (sections) {
        this.updateCartDrawerSections(sections);
      }
    })
    .catch(error => {
      console.error('Bulk add failed:', error);
      localStorage.removeItem('hiddenGiftCards');

      if (callback) {
        callback();
      } else {
        this.refreshCartDrawer();
      }
    });
  }

  bulkAddDonations(donationData, callback) {
    const items = donationData.map(item => ({
      id: item.variant_id,
      quantity: item.quantity,
      properties: item.properties || {}
    }));

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    })
    .then(response => response.json())
    .then(addResult => {
      localStorage.removeItem('hiddenDonations');

      if (addResult.items && addResult.items.length > 0) {
        this.classList.remove('is-empty');
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          cartDrawerItems.classList.remove('is-empty');
        }
      }

      if (callback) {
        callback();
        return Promise.resolve();
      }

      return fetch(`${window.routes.cart_url}?sections=cart-drawer,header`);
    })
    .then(response => {
      if (response && response.json) {
        return response.json();
      }
      return null;
    })
    .then(sections => {
      if (sections) {
        this.updateCartDrawerSections(sections);
      }
    })
    .catch(error => {
      console.error('Bulk add donations failed:', error);
      localStorage.removeItem('hiddenDonations');

      if (callback) {
        callback();
      } else {
        this.refreshCartDrawer();
      }
    });
  }

  updateCartDrawerSections(sections) {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        if (sections['cart-drawer']) {
          const sectionDOM = this.getSectionDOM(sections['cart-drawer']);
          const cartDrawerElement = sectionDOM.querySelector('cart-drawer');
          const hasEmptyClass = cartDrawerElement && cartDrawerElement.classList.contains('is-empty');

        if (hasEmptyClass) {
          this.classList.add('is-empty');
          const existingCartDrawerItems = document.querySelector('cart-drawer-items');
          if (existingCartDrawerItems) {
            existingCartDrawerItems.classList.add('is-empty');
          }
        } else {
          this.classList.remove('is-empty');
          const existingCartDrawerItems = document.querySelector('cart-drawer-items');
          if (existingCartDrawerItems) {
            existingCartDrawerItems.classList.remove('is-empty');
          }
        }

        const drawerElement = document.querySelector('#CartDrawer');
        if (drawerElement) {
          drawerElement.innerHTML = cartDrawerElement ? cartDrawerElement.querySelector('#CartDrawer').innerHTML : '';
        }
      }

      const cartIcon = document.querySelector('#cart-icon-bubble');
        if (cartIcon && sections['header']) {
          const headerDOM = this.getSectionDOM(sections['header']);
          const newCartIcon = headerDOM.querySelector('#cart-icon-bubble');
          if (newCartIcon) {
            cartIcon.innerHTML = newCartIcon.innerHTML;
          }
        }
        this.updateCheckoutAvailabilityState();
        // Dispatch cart-drawer:rendered event so custom kit data is restored
        document.dispatchEvent(new CustomEvent('cart-drawer:rendered'));
        resolve();
      });
    });
  }

  async refreshAndGetUnavailableCount() {
    try {
      if (typeof this.refreshCartDrawer === 'function') {
        await Promise.resolve(this.refreshCartDrawer());
      }

      // Wait a frame so DOM updates from refresh are applied before reading
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (typeof this.getUnavailableCount === 'function') {
        return await this.getUnavailableCount();
      }
    } catch (error) {
      console.error('Error refreshing cart drawer before checkout:', error);
    }

    const unavailableEl = document.getElementById('CartDrawer-UnavailableCount');
    return Number(unavailableEl?.dataset?.unavailableCount || 0);
  }

  async blockCheckoutForCustomKit() {
    const unavailableCount = await this.refreshAndGetUnavailableCount();
    if (unavailableCount > 0) {

      return { blocked: true, unavailableCount };
    }

    return { blocked: false, unavailableCount };
  }

  async refreshCartDrawer() {
    try {
      const response = await fetch(`${window.routes.cart_url}?sections=cart-drawer,header`);
      const sections = await response.json();
      await this.updateCartDrawerSections(sections);
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
      window.location.reload();
    }
  }

  isInternationalLocale() {
    const path = window.location.pathname;

    if (path === '/') return false;

    const localePattern = /^\/[a-z]{2}(-[a-z]{2})?(?:\/|$)/;
    return localePattern.test(path);
  }

  getCurrentLocaleRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  getBundleRowName(bundleRow) {
    const nameEl = bundleRow?.querySelector('.cart-item__name');
    if (!nameEl) return 'bundle';

    const nameClone = nameEl.cloneNode(true);
    nameClone.querySelectorAll('.cart-line-item__oos').forEach(el => el.remove());

    return nameClone.textContent?.replace(/\s+/g, ' ').trim() || 'bundle';
  }

  getLocaleCartSnapshotKey() {
    return 'tarte:locale-cart-snapshot:v2';
  }

  getCurrentCountryCode() {
    return window.Shopify?.country || document.documentElement.dataset.country || null;
  }

  getStoredLocaleCartSnapshot() {
    try {
      const snapshot = sessionStorage.getItem(this.getLocaleCartSnapshotKey());
      return snapshot ? JSON.parse(snapshot) : null;
    } catch (error) {
      console.error('Error reading locale cart snapshot:', error);
      return null;
    }
  }

  getLiveCartItemIdentifiers(cart) {
    const itemKeys = new Set();
    const bundleKeys = new Set();

    (cart?.items || []).forEach(item => {
      if (item.key) itemKeys.add(item.key);
      const properties = this.getCartItemBundleProperties(item);
      if (properties._bundle_key) bundleKeys.add(properties._bundle_key);
    });

    return { itemKeys, bundleKeys };
  }

  getCartItemBundleProperties(item) {
    const firstComponentProperties = item?.item_components?.[0]?.properties || {};
    return {
      ...firstComponentProperties,
      ...(item?.properties || {})
    };
  }

  buildBundleRecoveryModels(cart) {
    const bundleRows = Array.from(document.querySelectorAll(
      '#CartDrawer .cart-item[data-is-bundle="true"][data-item-key]'
    ));

    return (cart?.items || []).reduce((models, item) => {
      const properties = this.getCartItemBundleProperties(item);
      const bundleRow = bundleRows.find(row => row.dataset.itemKey === item.key);
      const isBundle = Boolean(
        properties._bundle_key
        || properties._bundle_components
        || bundleRow
      );
      if (!isBundle) return models;

      const isCustomKit = properties._is_custom_kit === true
        || properties._is_custom_kit === 'true';

      models.push({
        itemKey: item.key || null,
        bundleKey: properties._bundle_key || bundleRow?.dataset.bundleKey || null,
        name: properties._bundle_name || item.product_title || item.title || this.getBundleRowName(bundleRow),
        type: isCustomKit ? 'custom-kit' : 'bundle',
        kitUrl: isCustomKit
          ? properties._custom_kit_url || bundleRow?.dataset.customKitUrl || null
          : null
      });

      return models;
    }, []);
  }

  storeLocaleCartSnapshot(cart) {
    if (!cart?.items) return;

    try {
      sessionStorage.setItem(this.getLocaleCartSnapshotKey(), JSON.stringify({
        country: this.getCurrentCountryCode(),
        capturedAt: Date.now(),
        bundles: this.buildBundleRecoveryModels(cart)
      }));
      this._localeCartSnapshotRevision += 1;
    } catch (error) {
      console.error('Error saving locale cart snapshot:', error);
    }
  }

  async captureLocaleCartSnapshot(cart = null) {
    if (!this._localeCartSnapshotReconciled) return;

    try {
      const liveCart = cart?.items
        ? cart
        : await fetch(`${this.getCurrentLocaleRoot()}cart.js`, { cache: 'no-store' }).then(response => response.json());
      this.storeLocaleCartSnapshot(liveCart);
    } catch (error) {
      console.error('Error capturing locale cart snapshot:', error);
    }
  }

  async reconcileLocaleCartSnapshot() {
    const previousSnapshot = this.getStoredLocaleCartSnapshot();
    const currentCountry = this.getCurrentCountryCode();
    const snapshotRevisionAtStart = this._localeCartSnapshotRevision;
    let liveCart;

    try {
      liveCart = await fetch(
        `${this.getCurrentLocaleRoot()}cart.js`,
        { cache: 'no-store' }
      ).then(response => response.json());
    } catch (error) {
      console.error('Error reconciling locale cart snapshot:', error);
      this._localeCartSnapshotReconciled = true;
      return false;
    }

    let modalShown = false;
    if (
      previousSnapshot?.country
      && currentCountry
      && previousSnapshot.country !== currentCountry
      && Array.isArray(previousSnapshot.bundles)
    ) {
      const { itemKeys, bundleKeys } = this.getLiveCartItemIdentifiers(liveCart);
      const removedBundles = previousSnapshot.bundles.filter(bundle => {
        if (bundle.bundleKey) return !bundleKeys.has(bundle.bundleKey);
        if (bundle.itemKey) return !itemKeys.has(bundle.itemKey);
        return false;
      });

      if (removedBundles.length) {
        modalShown = this.showUnavailableKitItemsModal(
          removedBundles.map(bundle => ({
            name: bundle.name,
            recoveryModel: bundle
          }))
        );
      }
    }

    this._localeCartSnapshotReconciled = true;
    if (this._localeCartSnapshotRevision === snapshotRevisionAtStart) {
      this.storeLocaleCartSnapshot(liveCart);
    }
    return modalShown;
  }

  async getUnavailableCustomKitItemsByKit() {
    const targetRoot = this.getCurrentLocaleRoot();

    const bundleEntries = document.querySelectorAll(
      '.cart-item[data-is-custom-kit="true"] .cart-item__bundle-entry[data-product-handle]'
    );
    if (!bundleEntries.length) return;

    const handles = [...new Set(
      Array.from(bundleEntries).map(el => el.dataset.productHandle).filter(Boolean)
    )];

    const productsByHandle = {};
    await Promise.all(handles.map(async (handle) => {
      try {
        const res = await fetch(
          `${window.Shopify.routes.root}products/${handle}.js?_=${Date.now()}`,
          { cache: 'no-store' }
        );

        productsByHandle[handle] = res.ok
          ? { ...(await res.json()), found_in_market: true }
          : { available: false, variants: [], found_in_market: false };
      } catch (err) {
        console.error(`checkCustomKitItemAvailability: failed to fetch ${handle}`, err);
      }
    }));

    const unavailableByKit = new Map();
    Array.from(bundleEntries).forEach(el => {
      const product = productsByHandle[el.dataset.productHandle];
      if (!product) return;

      const variantId = Number(el.dataset.variantId);
      const variant = product.variants?.find(v => v.id === variantId);
      const bundleVariantTitleEl = el.querySelector('.cart-item__bundle-variant-title');
      const isHazmatVariant = bundleVariantTitleEl?.dataset.variantIsHazmat === 'true';

      const isUnavailable = product.found_in_market === false || (isHazmatVariant && this.isInternationalLocale());
      if (!isUnavailable) return;

      const kitRow = el.closest('.cart-item[data-is-custom-kit="true"]');
      if (!kitRow) return;

      if (!unavailableByKit.has(kitRow)) unavailableByKit.set(kitRow, []);
      const isVariantLevelOnly = product.available && isHazmatVariant && this.isInternationalLocale();
      unavailableByKit.get(kitRow).push({ el, variantTitle: isVariantLevelOnly ? variant.title : null });

    });

    return unavailableByKit;
  }

  async checkCustomKitItemAvailability(options = {}) {
    if (this._unavailableKitModalOpen) return false;

    if (!options.ignoreSuppression && sessionStorage.getItem('kitAvailabilityCheckSuppressed')) {
      sessionStorage.removeItem('kitAvailabilityCheckSuppressed');
      return false;
    }

    const unavailableByKit = await this.getUnavailableCustomKitItemsByKit(options);

    if (unavailableByKit.size > 0) {
      const firstKitItems = unavailableByKit.values().next().value;
      this.showUnavailableKitItemsModal(firstKitItems);
      return true;
    }

    return false;
  }

  getUnavailableCustomKitEditUrl(kitUrl) {
    const url = new URL(kitUrl, window.location.origin);
    url.searchParams.delete('bundle_key');
    url.searchParams.delete('ck_line_key');
    url.searchParams.set('oos_edit', Date.now().toString());

    let cleanPath = url.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?\//, '/');
    if (!cleanPath.startsWith('/')) cleanPath = '/' + cleanPath;

    const localeRoot = this.getCurrentLocaleRoot();
    const localePrefix = localeRoot === '/' ? '' : localeRoot.replace(/\/$/, '');
    url.pathname = `${localePrefix}${cleanPath}`;

    return url.toString();
  }

  async removeUnavailableBundle(bundleSource) {
    const root = this.getCurrentLocaleRoot();
    const itemKey = bundleSource?.dataset?.itemKey || bundleSource?.itemKey;
    const bundleKey = bundleSource?.dataset?.bundleKey || bundleSource?.bundleKey;
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
    let updatedCart = null;
    let itemKeyRemovalError = null;

    if (itemKey) {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          id: itemKey,
          quantity: 0,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });
      updatedCart = await response.json().catch(() => null);

      if (!response.ok || updatedCart?.errors) {
        itemKeyRemovalError = new Error(
          updatedCart?.errors || `Unable to remove bundle: ${response.status}`
        );
        updatedCart = null;
      }
    }

    if (!updatedCart && (itemKey || bundleKey)) {
      const cart = await fetch(`${root}cart.js`, { cache: 'no-store' }).then(r => r.json());
      const updates = {};
      (cart.items || []).forEach(item => {
        const properties = this.getCartItemBundleProperties(item);
        if (
          (itemKey && item.key === itemKey)
          || (bundleKey && properties._bundle_key === bundleKey)
        ) {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length) {
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ updates })
        });
        updatedCart = await response.json().catch(() => null);

        if (!response.ok || updatedCart?.errors) {
          throw new Error(updatedCart?.errors || `Unable to remove bundle: ${response.status}`);
        }
      } else if (itemKeyRemovalError) {
        console.info(
          'Bundle line was already absent from the localized cart:',
          itemKeyRemovalError.message
        );
      }
    }

    if (updatedCart && typeof window.updateTarteCartData === 'function') {
      await window.updateTarteCartData(updatedCart);
    }

    return updatedCart;
  }

  showUnavailableKitItemsModal(unavailableKitItems) {
    const modal = document.querySelector('#UnavailableKitItems');
    if (!modal) return;

    const recoveryModels = unavailableKitItems
      .map(item => item.recoveryModel)
      .filter(Boolean);
    const recoveryModel = recoveryModels[0];
    const kitRow = unavailableKitItems[0]?.kitRow
      || unavailableKitItems[0]?.el?.closest('.cart-item[data-is-custom-kit="true"]');
    if (!kitRow && !recoveryModel) return false;

    const isSingleCustomKit = recoveryModels.length === 0
      || (
        recoveryModels.length === 1
        && recoveryModel.type === 'custom-kit'
      );
    const kitUrl = isSingleCustomKit
      ? recoveryModel?.kitUrl || kitRow?.dataset.customKitUrl
      : null;

    const disclaimer = modal.querySelector('.common-modal__disclaimer');
    if (disclaimer) {
      disclaimer.textContent = recoveryModels.length
        ? disclaimer.dataset.bundleRecoveryText
        : disclaimer.dataset.customKitText;
    }

    const list = modal.querySelector('#UnavailableKitItemsList');
    if (list) {
      list.innerHTML = '';
      unavailableKitItems.forEach(({ el, name: itemName, variantTitle }) => {
        const name = itemName
                  || el?.querySelector('.cart-item__bundle-title')?.textContent?.trim()
                  || this.getBundleRowName(kitRow);
        const li = document.createElement('li');
        li.textContent = name;
        if (variantTitle) {
          const variantEl = document.createElement('span');
          variantEl.className = 'unavailable-kit-item__variant';
          variantEl.textContent = variantTitle;
          li.appendChild(variantEl);
        }
        list.appendChild(li);
      });
    }

    const completeUnavailableBundleRecovery = async ({ editKit = false } = {}) => {
      if (this._unavailableKitNavigationInProgress) return;
      this._unavailableKitNavigationInProgress = true;

      try {
        const cleanupTargets = recoveryModels.length
          ? recoveryModels
          : [kitRow].filter(Boolean);
        const cleanupErrors = [];

        for (const cleanupTarget of cleanupTargets) {
          try {
            await this.removeUnavailableBundle(cleanupTarget);
          } catch (error) {
            cleanupErrors.push(error);
            console.error('Error removing unavailable bundle:', error);
          }
        }

        if (cleanupErrors.length) {
          throw cleanupErrors[0];
        }

        if (editKit && kitUrl) {
          const editUrl = this.getUnavailableCustomKitEditUrl(kitUrl);
          const kitUrlObj = new URL(editUrl, window.location.origin);
          const pathSegments = kitUrlObj.pathname.split('/').filter(Boolean);
          const pageHandle = pathSegments[pathSegments.length - 1];
          sessionStorage.removeItem(`custom-kit:pending-selection:${pageHandle}`);
          sessionStorage.setItem('kitAvailabilityCheckSuppressed', '1');
          window.location.assign(editUrl);
        } else {
          this._unavailableKitNavigationInProgress = false;
        }
      } catch (err) {
        console.error('Error completing unavailable bundle recovery:', err);
        this._unavailableKitNavigationInProgress = false;
      }
    };

    const editBtn = modal.querySelector('#UnavailableKitEditBtn');
    if (editBtn && kitUrl) {
      editBtn.style.display = '';
      editBtn.textContent = editBtn.dataset.editKitText;
      editBtn.onclick = (e) => {
        e.preventDefault();
        completeUnavailableBundleRecovery({ editKit: true });
      };
    } else if (editBtn && recoveryModels.length) {
      editBtn.style.display = '';
      editBtn.textContent = editBtn.dataset.continueText;
      editBtn.onclick = (e) => {
        e.preventDefault();
        modal.hide();
      };
    } else if (editBtn) {
      editBtn.style.display = 'none';
    }

    const onModalClosed = (e) => {
      if (e.detail !== 'UnavailableKitItems') return;
      document.body.removeEventListener('modalClosed', onModalClosed);
      this._unavailableKitModalCloseHandler = null;
      this._unavailableKitModalOpen = false;
      completeUnavailableBundleRecovery({ editKit: Boolean(kitUrl) });
    };
    document.body.addEventListener('modalClosed', onModalClosed);

    modal.show();
  }

  async removeRestrictedItemsBeforeLocaleSwitch(localeLink) {
    const selectedLocale = localeLink.dataset.value;
    const usLocales = ['en', 'en-US', 'en-us', 'US'];
    const switchingToUS = usLocales.includes(selectedLocale);
    const currentLocaleRoot = this.getCurrentLocaleRoot();

    try {
      const cart = await fetch(
        `${currentLocaleRoot}cart.js`,
        { cache: 'no-store' }
      ).then(response => response.json());
      this.storeLocaleCartSnapshot(cart);

      if (switchingToUS) return;

      const giftCardItems = cart.items.filter(item =>
        item.product_type === 'Gift Card' ||
        item.gift_card === true
      );
      const donationItems = cart.items.filter(item =>
        item.product_type === 'Donation'
      );
      const itemsToRemove = [...giftCardItems, ...donationItems];

      if (!itemsToRemove.length) return;

      if (giftCardItems.length) {
        localStorage.setItem('hiddenGiftCards', JSON.stringify(giftCardItems.map(item => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {}
        }))));
      }

      if (donationItems.length) {
        localStorage.setItem('hiddenDonations', JSON.stringify(donationItems.map(item => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {}
        }))));
      }

      const updates = {};
      itemsToRemove.forEach(item => {
        updates[item.key] = 0;
      });

      const response = await fetch(`${currentLocaleRoot}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error(`Unable to remove restricted items: ${response.status}`);
      }
    } catch (error) {
      console.error('Error processing gift cards and donations:', error);
    }
  }

  setupLocaleSwitch() {
    window.prepareCartForLocaleSwitch = this.removeRestrictedItemsBeforeLocaleSwitch.bind(this);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const el = document.querySelector(section.selector);
      if (el && parsedState[section.section]) {
        el.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
      }
    });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.checkForRestrictedItems();
      cartDrawer.checkCustomKitItemAvailability();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('DOMContentLoaded', async () => {
  if (!window.cartDrawer) {
    window.cartDrawer = new CartDrawerItems();
  }

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const recoveredRemovedKit = await cartDrawer.reconcileLocaleCartSnapshot();
    cartDrawer.checkForRestrictedItems();
    cartDrawer.updateCheckoutAvailabilityState();
    if (!recoveredRemovedKit) {
      cartDrawer.checkCustomKitItemAvailability();
    }
  }

  // Ensure OOS state is re-applied after any cart render/update event (i.e. add to bag event)
  document.addEventListener('cart-items:updated', () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.updateCheckoutAvailabilityState) {
      drawer.updateCheckoutAvailabilityState();
    }
  });
});

document.addEventListener('tarte:cart-data-updated', (event) => {
  const cartDrawer = document.querySelector('cart-drawer');
  cartDrawer?.captureLocaleCartSnapshot(event.detail?.cartData);
});

document.addEventListener('cart-drawer:rendered', () => {
  const cartDrawer = document.querySelector('cart-drawer');
  cartDrawer?.captureLocaleCartSnapshot();
});

if (!window.cartDrawer) {
  window.cartDrawer = new CartDrawerItems();
}

window.addEventListener('popstate', (event) => {
  const cartDrawerEl = document.querySelector('cart-drawer');
  if (cartDrawerEl) {
    if (cartDrawerEl?.classList.contains('active') && (!event.state || !event.state.cartDrawerOpen)) {
      cartDrawerEl.close();
    }
  }
});
